import { ParentTypes } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { GetAllInternalAuditReportResultsResponse } from '@risksmart-app/trpc/src/types';
import type { GetAllInternalAuditReportResultsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAllInternalAuditReportResultsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetAllInternalAuditReportResultsArgs = Record<string, never>;

/**
 * V2 selects the `parents` relation twice per result table with different
 * ParentType filters (`internalAuditReports` / `documents` / `obligations` /
 * `risks` aliases). Drizzle cannot alias one relation twice, so the tRPC row
 * carries parents once (unfiltered, with all nested relations) and this mapper
 * rebuilds the aliased arrays the register consumers read.
 */
export const useGetAllInternalAuditReportResults = createQueryHook<
  UseGetAllInternalAuditReportResultsArgs,
  GetAllInternalAuditReportResultsResponse,
  GetAllInternalAuditReportResultsQuery
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.internalAuditResult.allInternalAuditReportResults.queryOptions(),
  mapTrpcDataToGraphQL: (data) => ({
    document_internal_audit_result: data.documentResults.map((result) => ({
      ...result,
      __typename: ParentTypes.DocumentInternalAuditResult,
      internalAuditReports: result.parents.filter(
        (parent) => parent.internalAuditReport
      ),
      documents: result.parents.filter((parent) => parent.document),
    })),
    obligation_internal_audit_result: data.obligationResults.map((result) => ({
      ...result,
      __typename: ParentTypes.ObligationInternalAuditResult,
      internalAuditReports: result.parents.filter(
        (parent) => parent.internalAuditReport
      ),
      obligations: result.parents.filter((parent) => parent.obligation),
    })),
    risk_uncontrolled_internal_audit_result: data.riskUncontrolledResults.map(
      (result) => ({
        ...result,
        __typename: ParentTypes.RiskUncontrolledInternalAuditResult,
        internalAuditReports: result.parents.filter(
          (parent) => parent.internalAuditReport
        ),
        risks: result.parents.filter((parent) => parent.risk),
      })
    ),
    risk_controlled_internal_audit_result: data.riskControlledResults.map(
      (result) => ({
        ...result,
        __typename: ParentTypes.RiskControlledInternalAuditResult,
        internalAuditReports: result.parents.filter(
          (parent) => parent.internalAuditReport
        ),
        risks: result.parents.filter((parent) => parent.risk),
      })
    ),
  }),
  graphqlDocument: GetAllInternalAuditReportResultsDocument,
});
